import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const pollStatus = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }

      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (res.data.payment_status === "paid") {
          setStatus("success");
        } else if (res.data.status === "expired") {
          setStatus("error");
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(attempts + 1), 2000);
        } else {
          setStatus("pending");
        }
      } catch (error) {
        console.error(error);
        if (attempts < 3) {
          setTimeout(() => setAttempts(attempts + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    pollStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="payment-success-page">
      <div className="page-container text-center py-24">
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-muted-foreground mx-auto mb-6" strokeWidth={1} />
            <h1 className="text-3xl md:text-4xl font-heading font-light mb-4">
              Vérification du paiement...
            </h1>
            <p className="text-muted-foreground">
              Merci de patienter quelques instants.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" strokeWidth={1} />
            <h1 className="text-3xl md:text-4xl font-heading font-light mb-4">
              Paiement confirmé !
            </h1>
            <p className="text-muted-foreground font-light max-w-md mx-auto mb-8">
              Merci pour votre paiement. Vous recevrez un email de confirmation avec tous les détails.
            </p>
            <Link to="/">
              <Button className="btn-primary">
                Retour à l'accueil
              </Button>
            </Link>
          </>
        )}

        {status === "pending" && (
          <>
            <div className="w-16 h-16 border-2 border-muted-foreground rounded-full mx-auto mb-6 flex items-center justify-center">
              <span className="text-2xl">?</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-light mb-4">
              Paiement en cours de traitement
            </h1>
            <p className="text-muted-foreground font-light max-w-md mx-auto mb-8">
              Votre paiement est en cours de vérification. Vous recevrez un email de confirmation une fois le paiement validé.
            </p>
            <Link to="/">
              <Button className="btn-secondary">
                Retour à l'accueil
              </Button>
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 border-2 border-destructive rounded-full mx-auto mb-6 flex items-center justify-center">
              <span className="text-2xl text-destructive">!</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-light mb-4">
              Une erreur est survenue
            </h1>
            <p className="text-muted-foreground font-light max-w-md mx-auto mb-8">
              Nous n'avons pas pu vérifier votre paiement. Si vous avez été débité, contactez-moi directement.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/contact">
                <Button className="btn-primary">
                  Me contacter
                </Button>
              </Link>
              <Link to="/">
                <Button className="btn-secondary">
                  Retour à l'accueil
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
